import React, {
  createRef,
  useCallback,
  useLayoutEffect,
  useEffect,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import debounce from 'lodash/debounce';
import find from 'lodash/find';
import get from 'lodash/get';

import { Link } from 'formlabs-web-components/Link';

import useWindowSize from '../../hooks/useWindowSize';

import './HeaderMenu.module.css';

const MobileSidescrollMenu = ({ menu }) => {
  const { t } = useTranslation();
  const { width: windowWidth } = useWindowSize();
  const [hasLeftOverflow, setLeftOverflowing] = useState(false);
  const [hasRightOverflow, setRightOverflowing] = useState(false);
  const listRef = createRef();

  // keep active item in center
  const activeEntryId = get(find(menu, { active: true }), 'name');
  const refs = menu.reduce((acc, entry) => {
    acc[entry.name] = createRef();
    return acc;
  }, {});
  useEffect(
    () => {
      if (activeEntryId) {
        refs[activeEntryId].current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    },
    [activeEntryId]
  );

  // toggle overflow gradients
  const scrollCallback = useCallback(
    debounce(newValue => {
      if (listRef.current) {
        const { scrollWidth, scrollLeft, clientWidth } = listRef.current;

        const isScrolledRight = clientWidth + scrollLeft >= scrollWidth;
        const isScrolledLeft = scrollLeft === 0;
        const isOverflowing = listRef.current.scrollWidth > windowWidth;

        setLeftOverflowing(!isScrolledLeft && isOverflowing);
        setRightOverflowing(!isScrolledRight && isOverflowing);
      }
    }, 200),
    [listRef, windowWidth]
  );
  useLayoutEffect(
    () => {
      if (listRef.current) {
        listRef.current.addEventListener('scroll', scrollCallback);
        scrollCallback();
      }

      return () => {
        if (listRef.current) {
          listRef.current.removeEventListener('scroll', scrollCallback);
        }
      };
    },
    [listRef]
  );

  return (
    <nav
      styleName={classnames('page-sub-nav', 'in-store', 'sidescroller', {
        'has-right-overflow': hasRightOverflow && !hasLeftOverflow,
        'has-left-overflow': hasLeftOverflow && !hasRightOverflow,
        'has-overflow': hasLeftOverflow && hasRightOverflow
      })}
      aria-label={t('Second level page navigation')}
    >
      <ul ref={listRef}>
        {menu.map(item => {
          return (
            <li
              key={item.url}
              ref={refs[item.name]}
              styleName={classnames({
                'current-page': item.active
              })}
            >
              <Link to={item.url} title={item.name}>
                {item.name}
              </Link>
            </li>
          );
        })}
        <li />
      </ul>
    </nav>
  );
};

export default MobileSidescrollMenu;
